import { useCallback, useEffect, useRef, useState } from 'react';

import type { WalletLoaded } from '@sorare/wallet-shared/src/contexts/messaging';

import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSetSolanaAddress } from '#src/contexts/wallet/Wallet/useSetSolanaAddress.tsx';
import { useMessagingContext } from '#src/contexts/wallet/index.ts';
import {
  WalletTab,
  useWalletDrawerContext,
} from '#src/contexts/walletDrawer/index.tsx';

import { useOAuthEthWallet } from './useOAuthEthWallet.ts';

export const useCreateAndLoadWallet = () => {
  const { currentUser } = useCurrentUserContext();
  const { needsCreateEthWallet } = useOAuthEthWallet();
  const { registerHandler } = useMessagingContext();
  const setSolanaAddress = useSetSolanaAddress();
  const { closeWalletAndDrawer, showWallet, setCurrentTab } =
    useWalletDrawerContext();
  const [initialValueOfNeedsCreateEthWallet] = useState(needsCreateEthWallet);
  const resolveRef = useRef<() => void>(undefined);

  const promise = useCallback(async () => {
    return new Promise<void>(resolve => {
      if (!initialValueOfNeedsCreateEthWallet) {
        resolve();
      } else {
        resolveRef.current = resolve;
        showWallet();
        setCurrentTab(WalletTab.GENERATE_KEYS);
      }
    });
  }, [initialValueOfNeedsCreateEthWallet, setCurrentTab, showWallet]);

  useEffect(() => {
    registerHandler<WalletLoaded>('walletLoaded', async () => {
      closeWalletAndDrawer();
      if (currentUser?.wallet && !currentUser.wallet?.solanaAddress) {
        await setSolanaAddress();
      }
      resolveRef.current?.();
      return {};
    });
  }, [
    registerHandler,
    closeWalletAndDrawer,
    setSolanaAddress,
    currentUser?.wallet,
  ]);

  return promise;
};
